import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  useMarkAllRead,
  useMarkNotification,
} from '@/hooks/useMarkNotification';
import { useNotifications } from '@/hooks/useNotifications';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import { groupNotifications } from '@/lib/utils/notificationGroups';

/** The bell preview is an unfiltered notifications list — tabs, task filters
 *  and company search all live on the /activity-feed page. */
export function useNotificationsSheet(open: boolean) {
  const { getTranslation: t } = useTranslator({ page: 'notifications' });
  const bottomRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  // `GET /api/notifications` requires `network:read` — the caller (the bell
  // icon) already hides itself without it, but this is cheap insurance
  // against the hook being mounted from anywhere else.
  const canReadNetwork = usePermissions().network.canRead;

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isLoading } =
    useNotifications({
      tab: 'notifications',
      enabled: open && canReadNetwork,
    });

  const markAllMutation = useMarkAllRead();
  const markMutation = useMarkNotification();

  const allItems = data?.pages.flatMap(p => p.data) ?? [];
  const grouped = groupNotifications(allItems);
  const unreadCount = allItems.filter(
    n => n.status === 'UNREAD' || n.status === 'PENDING',
  ).length;

  async function handleMarkAllRead() {
    try {
      await markAllMutation.mutateAsync();
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('actionFailed'));
    }
  }

  async function handleMarkRead(id: string) {
    try {
      await markMutation.mutateAsync({ id, status: 'READ' });
      await qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('actionFailed'));
    }
  }

  useEffect(() => {
    if (!bottomRef.current || !hasNextPage || isFetchingNextPage) return;
    const obs = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { threshold: 0.1 },
    );
    obs.observe(bottomRef.current);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    t,
    allItems,
    grouped,
    unreadCount,
    isLoading,
    isFetchingNextPage,
    isMarkAllPending: markAllMutation.isPending,
    handleMarkAllRead,
    handleMarkRead,
    bottomRef,
  };
}
